import React, { useEffect, useState } from "react";
import { Drawer, Tabs, Descriptions, Badge, Space, Tag, Timeline, Button, Row, Col, Spin, Empty, Typography, Divider, message } from "antd";
import { useNavigate } from "react-router-dom";
import { useMonitor } from "../context/MonitorContext";
import { FlightTimeDisplay } from "../../../../components/DateTimeDisplay";
import { useTimeZone } from "../../../../contexts/TimeZoneContext";
import { AuditOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, LoadingOutlined, HistoryOutlined, InfoCircleOutlined } from "@ant-design/icons";
import useTimezoneConverter from "../../../../hooks/useTimezoneConverter";
import ReactMarkdown from "react-markdown";
import dayjs from "dayjs";
import PermissionGuard from "../../../../components/auth/PermissionGuard";
import FlightHistoryTimeline from "../../../../components/FlightHistoryTimeline/FlightHistoryTimeline";
import CargoReportTab from "./CargoReportTab";
import DiversionLegsSection from "./DiversionLegsSection";
import { RampChecklistTab } from "../../../rampChecklist";

const { Text } = Typography;

// 创建一个简单的时区缩写函数
const getSimpleTimezoneAbbr = (timezone) => {
    if (!timezone) return "";

    // 常见时区的简单缩写
    const abbrs = {
        UTC: "UTC",
        "Asia/Shanghai": "CST",
        "America/New_York": "EST",
        "America/Los_Angeles": "PST",
        "Europe/London": "GMT",
        LOCAL: "LCL",
    };

    return abbrs[timezone] || timezone.split("/").pop();
};

/**
 * 航班详情抽屉组件
 * 显示选中航班的详细信息、事件日志和关联事务
 */
const FlightDetailDrawer = ({ visible, onClose, agReviewState = { loading: false, error: "", items: [] } }) => {
    const { selectedFlight, loadingFlightEvents, flightEvents, fetchFlightEvents } = useMonitor();
    const { currentTimezone } = useTimeZone();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState("info");

    // 根据当前选中的航班获取时区转换器
    const { formatWithDayMark } = useTimezoneConverter(
        selectedFlight?.departure_airport,
        {}, // 实际项目中应传入airportsMap
    );

    useEffect(() => {
        if (visible && selectedFlight?.id) {
            fetchFlightEvents(selectedFlight.id);
        }
    }, [visible, selectedFlight?.id, fetchFlightEvents]);

    // 处理关联对象点击
    const handleRelatedObjectClick = (type, id, title) => {
        console.log("点击关联对象:", { type, id, title });

        // 根据类型处理不同的关联对象
        switch (type) {
            case "business":
                // 处理业务对象点击，可以打开相应的详情抽屉或模态框
                message.info(`将打开 ${title} 的详情页面`);
                // TODO: 实现具体的业务对象详情查看逻辑
                break;
            case "email":
                // 处理邮件对象点击，可以打开邮件查看器
                message.info(`将打开 ${title} 的邮件详情`);
                // TODO: 实现邮件详情查看逻辑
                break;
            default:
                console.warn("未知的关联对象类型:", type);
        }
    };

    if (!selectedFlight) {
        return null;
    }

    // 获取航班状态标签
    const getStatusBadge = (status) => {
        const statusConfig = {
            SCHEDULED: { status: "default", text: "已计划", icon: <ClockCircleOutlined /> },
            ACTIVE: { status: "processing", text: "执行中", icon: <LoadingOutlined /> },
            COMPLETED: { status: "success", text: "已完成", icon: <CheckCircleOutlined /> },
            CANCELLED: { status: "error", text: "已取消", icon: <CloseCircleOutlined /> },
            DELAYED: { status: "warning", text: "延误", icon: <ClockCircleOutlined /> },
            DIVERTED: { status: "warning", text: "备降", icon: <InfoCircleOutlined /> },
            REPLACED: { status: "default", text: "已替换", icon: <HistoryOutlined /> },
            PLAN_CANCELLED: { status: "error", text: "计划性取消", icon: <CloseCircleOutlined /> },
        };

        const config = statusConfig[status] || { status: "default", text: status, icon: null };
        return (
            <Badge
                status={config.status}
                text={
                    <Space>
                        {config.icon} {config.text}
                    </Space>
                }
            />
        );
    };

    // 渲染航班基本信息
    const renderFlightInfo = () => {
        const primaryReviewSignal = agReviewState.items[0];

        return (
            <>
            <Descriptions bordered column={1} size="small">
                <Descriptions.Item label="航班号">{selectedFlight.flight_number}</Descriptions.Item>
                <Descriptions.Item label="航班日期">{selectedFlight.flight_date}</Descriptions.Item>
                <Descriptions.Item label="状态">{getStatusBadge(selectedFlight.status)}</Descriptions.Item>
                <Descriptions.Item label="起飞机场">{selectedFlight.departure_airport}</Descriptions.Item>
                <Descriptions.Item label="到达机场">{selectedFlight.arrival_airport}</Descriptions.Item>
                <Descriptions.Item label="计划起飞时间 (STD)">
                    <FlightTimeDisplay utcTime={selectedFlight.std} airportCode={selectedFlight.departure_airport} format="YYYY-MM-DD HH:mm" showTimezone={true} />
                </Descriptions.Item>
                <Descriptions.Item label="计划到达时间 (STA)">
                    <FlightTimeDisplay utcTime={selectedFlight.sta} airportCode={selectedFlight.arrival_airport} format="YYYY-MM-DD HH:mm" showTimezone={true} />
                </Descriptions.Item>
                {selectedFlight.etd && (
                    <Descriptions.Item label="预计起飞时间 (ETD)">
                        <FlightTimeDisplay utcTime={selectedFlight.etd} airportCode={selectedFlight.departure_airport} format="YYYY-MM-DD HH:mm" showTimezone={true} />
                    </Descriptions.Item>
                )}
                {selectedFlight.eta && (
                    <Descriptions.Item label="预计到达时间 (ETA)">
                        <FlightTimeDisplay utcTime={selectedFlight.eta} airportCode={selectedFlight.arrival_airport} format="YYYY-MM-DD HH:mm" showTimezone={true} />
                    </Descriptions.Item>
                )}
                {selectedFlight.atd && (
                    <Descriptions.Item label="实际起飞时间 (ATD)">
                        <FlightTimeDisplay utcTime={selectedFlight.atd} airportCode={selectedFlight.departure_airport} format="YYYY-MM-DD HH:mm" showTimezone={true} />
                    </Descriptions.Item>
                )}
                {selectedFlight.ata && (
                    <Descriptions.Item label="实际到达时间 (ATA)">
                        <FlightTimeDisplay utcTime={selectedFlight.ata} airportCode={selectedFlight.arrival_airport} format="YYYY-MM-DD HH:mm" showTimezone={true} />
                    </Descriptions.Item>
                )}
                <Descriptions.Item label="飞机注册号">{selectedFlight.aircraft_reg || "暂未分配"}</Descriptions.Item>
                {selectedFlight.booking_no && (
                    <Descriptions.Item label="订单号">
                        <Tag color="geekblue">{selectedFlight.booking_no}</Tag>
                    </Descriptions.Item>
                )}
                {selectedFlight.delay_code && (
                    <Descriptions.Item label="延误代码">
                        <Tag color="orange">{selectedFlight.delay_code}</Tag>
                        {selectedFlight.delay_reason}
                    </Descriptions.Item>
                )}
                {selectedFlight.remarks && <Descriptions.Item label="备注">{selectedFlight.remarks}</Descriptions.Item>}
                {(agReviewState.loading || agReviewState.error || primaryReviewSignal) && (
                    <Descriptions.Item label="邮件审核">
                        {agReviewState.loading ? (
                            <Space><Spin size="small" /> <Text type="secondary">正在检查审核信号</Text></Space>
                        ) : agReviewState.error ? (
                            <Text type="secondary">审核入口暂时无法加载</Text>
                        ) : (
                            <Space direction="vertical" size={8}>
                                <Space wrap>
                                    <Tag color="gold">待审核</Tag>
                                    {agReviewState.items.length > 1 ? <Tag>{agReviewState.items.length} 项</Tag> : null}
                                    <Text>{primaryReviewSignal.summary}</Text>
                                </Space>
                                <Button
                                    size="small"
                                    type="primary"
                                    icon={<AuditOutlined />}
                                    onClick={() => navigate(`/review/ag/${primaryReviewSignal.reviewCaseId}`)}
                                >
                                    打开审核工作台
                                </Button>
                            </Space>
                        )}
                    </Descriptions.Item>
                )}

                {selectedFlight.replaced_by_flight_id && (
                    <Descriptions.Item label="被替换信息">
                        替换航班ID: {selectedFlight.replaced_by_flight_id}
                        <br />
                        替换时间: {selectedFlight.replaced_at}
                        <br />
                        替换原因: {selectedFlight.replaced_reason || "未指定"}
                    </Descriptions.Item>
                )}
            </Descriptions>

            <DiversionLegsSection flight={selectedFlight} onNavigateFlight={() => message.info("请从航班列表点击该航段查看详情")} />
            </>
        );
    };

    // 根据事件类型返回对应的图标和颜色
    const getEventIcon = (eventType) => {
        const iconMap = {
            PLAN_INITIATED: { icon: <InfoCircleOutlined />, color: "blue" },
            PLAN_REPLACEMENT: { icon: <HistoryOutlined />, color: "purple" },
            STATUS_CHANGED: { icon: <ClockCircleOutlined />, color: "orange" },
            TIME_UPDATED: { icon: <ClockCircleOutlined />, color: "blue" },
            FLIGHT_ATTRIBUTE_UPDATED: { icon: <InfoCircleOutlined />, color: "cyan" },
            FUEL_REQUEST_CREATED: { icon: <InfoCircleOutlined />, color: "green" },
            FUEL_REQUEST_CONFIRMED: { icon: <CheckCircleOutlined />, color: "green" },
            PERMIT_REQUESTED: { icon: <InfoCircleOutlined />, color: "gold" },
            PERMIT_GRANTED: { icon: <CheckCircleOutlined />, color: "green" },
            AI_SUGGESTION_CREATED: { icon: <InfoCircleOutlined />, color: "blue" },
            AI_SUGGESTION_ACTIONED: { icon: <CheckCircleOutlined />, color: "blue" },
            MVT_RECEIVED: { icon: <InfoCircleOutlined />, color: "purple" },
        };

        return iconMap[eventType] || { icon: <InfoCircleOutlined />, color: "gray" };
    };

    // 渲染事件日志时间轴（保留作为备用，现在使用新的时间线组件）
    const renderEventTimeline = () => {
        if (loadingFlightEvents) {
            return <Spin tip="加载事件日志..." />;
        }

        if (!flightEvents || flightEvents.length === 0) {
            return <Empty description="暂无事件日志" />;
        }

        const timelineItems = flightEvents.map((event) => {
            const { icon, color } = getEventIcon(event.event_type);
            // 拆分日期和时间
            const dateStr = dayjs(event.created_at).format("YYYY-MM-DD");
            const timeStr = dayjs(event.created_at).format("HH:mm:ss");
            return {
                key: event.id,
                dot: icon,
                color: color,
                label: null, // 不再使用 label，时间放到内容区
                children: (
                    <div key={event.id} className="event-log-item" style={{ marginTop: -2 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 2 }}>
                            <Text type="secondary" style={{ fontSize: "12px" }}>
                                {dateStr} {timeStr}
                            </Text>
                            {event.operator && (
                                <>
                                    <Divider type="vertical" style={{ height: "16px", margin: 0 }} />
                                    <Text type="secondary" style={{ fontSize: "12px" }}>
                                        操作者: <span style={{ color: "#555" }}>{event.operator}</span>
                                    </Text>
                                </>
                            )}
                        </div>
                        {event.event_description && (
                            <div className="event-log-description markdown-content" style={{ marginBottom: 4 }}>
                                <ReactMarkdown>{event.event_description}</ReactMarkdown>
                            </div>
                        )}
                        <div className="event-log-meta" style={{ display: "flex", flexWrap: "wrap", gap: "0 12px", alignItems: "center" }}>
                            {event.field_name && (
                                <div className="event-log-field-change">
                                    <Text type="secondary" style={{ fontSize: "12px" }}>
                                        {event.field_name}:{" "}
                                    </Text>
                                    {event.previous_value && (
                                        <Text delete style={{ fontSize: "12px", color: "#ff7875" }}>
                                            {String(event.previous_value)}
                                        </Text>
                                    )}
                                    {event.previous_value && event.new_value && <span style={{ margin: "0 4px", color: "#aaa" }}> → </span>}
                                    {event.new_value && <Text style={{ fontSize: "12px", color: "#52c41a" }}>{String(event.new_value)}</Text>}
                                </div>
                            )}
                            {event.related_business_type && (
                                <Tag color="blue" style={{ fontSize: "11px", padding: "0 4px", height: "18px", lineHeight: "16px", margin: 0 }}>
                                    {event.related_business_type} #{event.related_business_id}
                                </Tag>
                            )}
                        </div>
                    </div>
                ),
            };
        });

        return <Timeline mode="left" items={timelineItems} style={{ marginTop: 16 }} />;
    };

    // 渲染新的历史时间线
    const renderHistoryTimeline = () => {
        return (
            <div style={{ height: "calc(100vh - 200px)", overflowY: "auto" }}>
                <FlightHistoryTimeline flightId={selectedFlight.id} onRelatedObjectClick={handleRelatedObjectClick} />
            </div>
        );
    };

    // 定义Tab项
    const tabItems = [
        {
            key: "info",
            label: "基本信息",
            children: renderFlightInfo(),
        },
        {
            key: "timeline",
            label: "变更历史",
            children: renderHistoryTimeline(),
        },
        {
            key: "events",
            label: "详细日志",
            children: renderEventTimeline(),
        },
        {
            key: "cargo",
            label: "载量报告",
            children: <CargoReportTab flightId={selectedFlight?.id} />,
        },
        {
            key: "rampChecklist",
            label: "过站检查单",
            // 仅在切到本标签时挂载，避免每次打开抽屉就为航段隐式生成检查单
            children: activeTab === "rampChecklist" ? <RampChecklistTab flightId={selectedFlight?.id} /> : null,
        },
    ];

    return (
        <Drawer title={`航班详情: ${selectedFlight.flight_number} (${selectedFlight.flight_date})`} placement="right" width={800} onClose={onClose} open={visible}>
            <PermissionGuard
                permission="flight_operation:read"
                fallback={
                    <div style={{ textAlign: "center", padding: "50px" }}>
                        <Empty description="您没有权限查看航班详情" />
                    </div>
                }
            >
                <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
            </PermissionGuard>
        </Drawer>
    );
};

export default FlightDetailDrawer;
